import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_ERA_ACTIONS = 'FETCH_ERA_ACTIONS';
export const FETCH_ERA_ACTIONS_SUCCESS = 'FETCH_ERA_ACTIONS_SUCCESS';
export const FETCH_ERA_ACTIONS_FAIL = 'FETCH_ERA_ACTIONS_FAIL';
export const CLEAR_ERA_ACTIONS = 'CLEAR_ERA_ACTIONS';
export const CREATE_ERA_ACTION = 'CREATE_ERA_ACTION';
export const CREATE_ERA_ACTION_SUCCESS = 'CREATE_ERA_ACTION_SUCCESS';
export const CREATE_ERA_ACTION_FAIL = 'CREATE_ERA_ACTION_FAIL';
export const UPDATE_ERA_ACTION = 'UPDATE_ERA_ACTION';
export const UPDATE_ERA_ACTION_SUCCESS = 'UPDATE_ERA_ACTION_SUCCESS';
export const UPDATE_ERA_ACTION_FAIL = 'UPDATE_ERA_ACTION_FAIL';
export const DELETE_ERA_ACTION = 'DELETE_ERA_ACTION';
export const DELETE_ERA_ACTION_SUCCESS = 'DELETE_ERA_ACTION_SUCCESS';
export const DELETE_ERA_ACTION_FAIL = 'DELETE_ERA_ACTION_FAIL';

// Reducer
export default function eraActionsReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_ERA_ACTIONS:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_ERA_ACTIONS_SUCCESS:
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading > 0 ? state.loading - 1 : 0 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case FETCH_ERA_ACTIONS_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_ERA_ACTIONS:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_ERA_ACTION_SUCCESS: {
        const newData = [
          ...state.data.slice(0, state.data.length),
          payload,
        ];
        return {
          ...state,
          count: state.count + 1,
          data: newData,
        };
      }
    case UPDATE_ERA_ACTION_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_ERA_ACTION_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchEraActions(payload) {
  return { type: FETCH_ERA_ACTIONS, payload };
}

export function fetchEraActionsSuccess(payload) {
  return { type: FETCH_ERA_ACTIONS_SUCCESS, payload };
}

export function fetchEraActionsFail(payload) {
  return { type: FETCH_ERA_ACTIONS_FAIL, payload };
}

export function clearEraActions(payload) {
  return { type: CLEAR_ERA_ACTIONS, payload };
}

export function updateEraAction(payload) {
  return { type: UPDATE_ERA_ACTION, payload };
}

export function updateEraActionSuccess(payload) {
  return { type: UPDATE_ERA_ACTION_SUCCESS, payload };
}

export function updateEraActionFail(payload) {
  return { type: UPDATE_ERA_ACTION_FAIL, payload };
}

export function createEraAction(payload) {
  return { type: CREATE_ERA_ACTION, payload };
}

export function createEraActionSuccess(payload) {
  return { type: CREATE_ERA_ACTION_SUCCESS, payload };
}

export function createEraActionFail(payload) {
  return { type: CREATE_ERA_ACTION_FAIL, payload };
}

export function deleteEraAction(payload) {
  return { type: DELETE_ERA_ACTION, payload };
}

export function deleteEraActionSuccess(payload) {
  return { type: DELETE_ERA_ACTION_SUCCESS, payload };
}

export function deleteEraActionFail(payload) {
  return { type: DELETE_ERA_ACTION_FAIL, payload };
}

// Requests

let abort;

export function requestFetchEraActions(payload) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = payload
    ? abortableCallApi(`claim_md/era_action_const/?limit=${payload.limit}&offset=${payload.offset}`)
    : abortableCallApi('claim_md/era_action_const/');
  abort = request.abort;
  return request.res;
}

export function requestSearchEraActions({ limit, offset, filters }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('claim_md/era_action_const/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestCreateEraAction(payload) {
  return callApi('claim_md/era_action_const/', 'POST', payload);
}

export function requestUpdateEraAction(payload) {
  return callApi('claim_md/era_action_const/', 'PATCH', payload);
}

export function requestDeleteEraAction(payload) {
  return callApi(`claim_md/era_action_const/?id=${payload}`, 'DELETE');
}

// Sagas
function* fetchEraActionsSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchEraActions, action.payload)
      : yield call(requestFetchEraActions, action.payload);
    yield put(fetchEraActionsSuccess({
      data: res.data,
      count: res.count,
      ...action.payload.push ? { push: true } : {},
      triggerLoading,
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchEraActionsFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createEraActionSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateEraAction, values);
    yield put(createEraActionSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New ERA Action successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createEraActionFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateEraActionSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateEraAction, values);
    yield put(updateEraActionSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'ERA Action successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateEraActionFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteEraActionSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteEraAction, ID);
    yield put(deleteEraActionSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'ERA Action successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteEraActionFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete ERA Action',
      variant: 'warning',
    }));
  }
}

export function* eraActionsSaga() {
  yield takeLatest(FETCH_ERA_ACTIONS, fetchEraActionsSaga);
  yield takeEvery(UPDATE_ERA_ACTION, updateEraActionSaga);
  yield takeEvery(CREATE_ERA_ACTION, createEraActionSaga);
  yield takeEvery(DELETE_ERA_ACTION, deleteEraActionSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/eraActions.js